/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace logistic
*/
var logistic = {};

/**
* @name cdf
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/cdf}
*/
import cdf from './../../../../base/dists/logistic/cdf';
setReadOnly( logistic, 'cdf', cdf );

/**
* @name Logistic
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/ctor}
*/
import Logistic from './../../../../base/dists/logistic/ctor';
setReadOnly( logistic, 'Logistic', Logistic );

/**
* @name entropy
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/entropy}
*/
import entropy from './../../../../base/dists/logistic/entropy';
setReadOnly( logistic, 'entropy', entropy );

/**
* @name kurtosis
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/kurtosis}
*/
import kurtosis from './../../../../base/dists/logistic/kurtosis';
setReadOnly( logistic, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/logcdf}
*/
import logcdf from './../../../../base/dists/logistic/logcdf';
setReadOnly( logistic, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/logpdf}
*/
import logpdf from './../../../../base/dists/logistic/logpdf';
setReadOnly( logistic, 'logpdf', logpdf );

/**
* @name mean
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/mean}
*/
import mean from './../../../../base/dists/logistic/mean';
setReadOnly( logistic, 'mean', mean );

/**
* @name median
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/median}
*/
import median from './../../../../base/dists/logistic/median';
setReadOnly( logistic, 'median', median );

/**
* @name mgf
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/mgf}
*/
import mgf from './../../../../base/dists/logistic/mgf';
setReadOnly( logistic, 'mgf', mgf );

/**
* @name mode
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/mode}
*/
import mode from './../../../../base/dists/logistic/mode';
setReadOnly( logistic, 'mode', mode );

/**
* @name pdf
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/pdf}
*/
import pdf from './../../../../base/dists/logistic/pdf';
setReadOnly( logistic, 'pdf', pdf );

/**
* @name quantile
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/quantile}
*/
import quantile from './../../../../base/dists/logistic/quantile';
setReadOnly( logistic, 'quantile', quantile );

/**
* @name skewness
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/skewness}
*/
import skewness from './../../../../base/dists/logistic/skewness';
setReadOnly( logistic, 'skewness', skewness );

/**
* @name stdev
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/stdev}
*/
import stdev from './../../../../base/dists/logistic/stdev';
setReadOnly( logistic, 'stdev', stdev );

/**
* @name variance
* @memberof logistic
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/logistic/variance}
*/
import variance from './../../../../base/dists/logistic/variance';
setReadOnly( logistic, 'variance', variance );


// EXPORTS //

export default logistic;
